import { AnswerPatchDto, AnswerSelectDto, SurveySelectDto } from '@@types/api.types.ts';
import { ApiErrorWrapper, useApi } from '@hooks/api.hook.tsx';
import { useMutation } from '@tanstack/react-query';
import { removeEmptyStrings } from '@utils/form-validator.util.ts';
import { AnswerResponseProgress } from '../types/answer.types';
import { queryClient } from './client';
import { queryKeys } from './keys';
import { useGetCurrentUser } from './users.query';

export const mapDisplayTextToEnum = (displayText: string): AnswerResponseProgress => {
  switch (displayText.toLowerCase()) {
    case 'yes':
      return AnswerResponseProgress.YES;
    case 'no':
      return AnswerResponseProgress.NO;
    case 'in progress':
      return AnswerResponseProgress.IN_PROGRESS;
    case 'not applicable':
      return AnswerResponseProgress.NOT_APPLICABLE;
    default:
      return displayText as AnswerResponseProgress;
  }
};

export const useInnovatorPatchAnswer = () => {
  const { isInnovator } = useGetCurrentUser();
  const onError = (error: Error) => {
    console.log('Error submitting answer:', error);
  };

  const onSuccess = (answer: AnswerSelectDto) => {
    queryClient.setQueryData(queryKeys.survey.getSurvey(answer.surveyId), (survey?: SurveySelectDto) => {
      if (!survey) return;

      const answers: AnswerSelectDto[] = JSON.parse(JSON.stringify(survey.answers));
      const answerIndex = answers.findIndex((a) => a.id === answer.id);
      if (answerIndex === -1) {
        answers.push(answer);
      } else {
        answers[answerIndex] = answer;
      }

      return { ...survey, answers };
    });

    queryClient.invalidateQueries({ queryKey: queryKeys.survey.getSurvey(answer.surveyId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.survey.getSurveys() });
  };

  const { apiPatch } = useApi();

  return useMutation<AnswerSelectDto, Error, AnswerPatchDto>({
    mutationFn: async (formData) => {
      if (!isInnovator)
        throw new ApiErrorWrapper({ errors: [{ message: 'Only Innovators can update answers', statusCode: 403 }] });

      // If the response is a display text, convert it to enum value
      if (formData.response) {
        formData.response = mapDisplayTextToEnum(formData.response);
      }

      const cleanedData = removeEmptyStrings(formData, true);
      return await apiPatch({ path: '/v1/innovator/answers/', body: cleanedData });
    },
    onError,
    onSuccess,
  });
};
